import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {
  AnalysisOutputStepType,
  NonExecutableStepType,
  isAnalysisOutputStepType,
  isNonExecutableStepType,
} from 'analysis-tool/AnalysisTypes';
import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import isArchived from 'archive/isArchived';
import {
  PipelineStepDataKind,
  PipelineStepType,
  PipelineStepStatusCode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {StepTypeInfo} from 'pipelines/hooks/useStepTypeInfosQuery';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {getPipelineStepRawDatasOfKind} from 'pipelines/StepDataUtils';
import {traversePipeline} from 'pipelines/StepLineageUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';

export type CanEditReturnValue = {canEdit: true; message: null} | {canEdit: false; message: string};

type NonRerunnableStepType = AnalysisOutputStepType | NonExecutableStepType;

export function isNonReRunnableStepType(stepType: PipelineStepType): stepType is NonRerunnableStepType {
  return isAnalysisOutputStepType(stepType) || isNonExecutableStepType(stepType);
}

export const NON_RERUNNABLE_STEP_TYPE_TO_USER_ERROR_MESSAGE: {
  [key in NonRerunnableStepType]: string;
} = {
  [PipelineStepType.BULK_IMPORT]: 'used to create Benchling objects',
  [PipelineStepType.CREATE_ENTITIES]: 'used to create entities',
  [PipelineStepType.EXPORT_DATA_CATALOG_DATASET]: 'used to create datasets',
  [PipelineStepType.EXPORT_DATA_CATALOG_FILE]: 'used to create files',
  [PipelineStepType.INSERT_RESULT_URL_IN_DOCUMENT]: 'used to insert a result URL in a document',
  [PipelineStepType.LEGACY_DATA_IMPORT_ENTRY_CREATION]: 'used to create notebook entries',
  [PipelineStepType.MOVE_INVENTORY_PLATES]: 'used to move inventory plates',
  [PipelineStepType.NDC_CREATE_PROTEINS_FROM_DATASET]: 'used to create proteins',
  [PipelineStepType.NOTEBOOK_TABLE_DATASET]: 'created from a Notebook table',
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: 'no longer supported',
  [PipelineStepType.PARAMETER_OPTIMIZATION_DATASET_CONFIGURATION]: 'no longer supported',
  [PipelineStepType.PARAMETER_OPTIMIZATION_MODELING]: 'no longer supported',
  [PipelineStepType.PARAMETER_OPTIMIZATION_PREDICTION]: 'no longer supported',
  [PipelineStepType.PROCEDURE_RUN_DATA_FRAMES]: 'no longer supported',
  [PipelineStepType.PUT_PLATES_IN_INVENTORY]: 'used to put plates in inventory',
  [PipelineStepType.SAVE_TO_RESULTS]: 'used to create results',
  [PipelineStepType.SEND_TO_DOCUMENT]: 'used to send data',
  [PipelineStepType.TEMPLATE_INPUT]: 'used as a static input table from a template',
  [PipelineStepType.THIRD_PARTY]: 'to export data',
  [PipelineStepType.UPDATE_ENTITIES]: 'used to update entities',
};

function checkPermissions(
  analysis: Pick<PipelineGraphQL, 'permissions' | 'archiveRecord'>
): CanEditReturnValue | null {
  // This message should not actually be shown to the user, because options to modify the analysis
  // should be hidden if the analysis is read-only. This check is included for completeness.
  if (isAnalysisReadOnly(analysis)) {
    return {
      canEdit: false,
      message: 'You do not have permissions to modify this analysis.',
    };
  }
  return null;
}

function checkHasDownstreamFlagRows(stepTypes: Set<PipelineStepType>): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.FLAG_ROWS)) {
    return {
      canEdit: false,
      message: 'This table cannot be replaced because it has flagged rows.',
    };
  }
  return null;
}

function checkStepIsRerunnable(baseMessage: string, step: PipelineStep): CanEditReturnValue | null {
  // Rerunnability is decided entirely by the backend: `isRerunnable` already accounts for the step's
  // descendant lineage, and `nonRerunnableReason` is the user-facing fragment for the blocking step (self
  // or descendant). The frontend just surfaces them; it does not re-derive the reason from the step type.
  if (!step.isRerunnable && step.nonRerunnableReason != null) {
    return {
      canEdit: false,
      message: `${baseMessage} it is ${step.nonRerunnableReason}.`,
    };
  }
  return null;
}

/**
 * Check if a step is an output step type (creates data outside the analysis).
 * These steps can never be modified or deleted.
 */
function checkIsOutputStepType(
  baseMessage: string,
  stepTypes: Set<PipelineStepType>
): CanEditReturnValue | null {
  for (const stepType of stepTypes) {
    if (isAnalysisOutputStepType(stepType)) {
      return {
        canEdit: false,
        message: `${baseMessage} it is ${NON_RERUNNABLE_STEP_TYPE_TO_USER_ERROR_MESSAGE[stepType]}.`,
      };
    }
  }
  return null;
}

function checkNotRegressionOrRegressionNotUsed(
  baseMessage: string,
  pipeline: Pick<PipelineGraphQL, 'id' | 'steps'>,
  step: PipelineStep
): CanEditReturnValue | null {
  const regressionSteps = traversePipeline(pipeline, {
    startingSteps: [step],
    checkMatchFn: (downstreamStep) => downstreamStep.stepType === PipelineStepType.REGRESSION,
  });

  for (const regressionStep of regressionSteps) {
    const isRegressionUsedDownstream =
      traversePipeline(pipeline, {
        startingSteps: [regressionStep],
        stopAtMatch: true,
        checkMatchFn: (downstreamStep) =>
          downstreamStep.stepType === PipelineStepType.REGRESSION_INTERPOLATION,
      }).length > 0;
    if (isRegressionUsedDownstream) {
      return {
        canEdit: false,
        message: `${baseMessage} it is used to create a regression that is used elsewhere in this analysis.`,
      };
    }
  }

  return null;
}

function checkHasRunningStep(steps: Array<PipelineStep>, baseMessage: string): CanEditReturnValue | null {
  const hasRunningStep = steps.find((step) => step.statusCode === PipelineStepStatusCode.RUNNING);
  if (hasRunningStep) {
    return {
      canEdit: false,
      message: `${baseMessage} the analysis is still running.`,
    };
  }
  return null;
}

function checkIsStepTypeUnsupported(
  step: PipelineStep,
  unsupportedStepTypes: Set<PipelineStepType>,
  baseMessage: string,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo>
): CanEditReturnValue | null {
  if (unsupportedStepTypes.has(step.stepType)) {
    return {
      canEdit: false,
      message: `${baseMessage} is not supported for ${getUserFacingStepTypeName(
        step.stepType,
        stepTypeInfos
      ).toLowerCase()} steps.`,
    };
  }
  return null;
}

function checkIsUsedToCreateNewTable(
  stepTypes: Set<PipelineStepType>,
  baseMessage: string
): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.PROMOTE_DATASET)) {
    return {
      canEdit: false,
      message: `${baseMessage} it is used to create a new table.`,
    };
  }

  return null;
}

function checkIsUsedInJoin(stepTypes: Set<PipelineStepType>, baseMessage: string): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.JOIN)) {
    return {
      canEdit: false,
      message: `${baseMessage} it is used in a join.`,
    };
  }

  return null;
}

function checkIsUsedInUnion(
  stepTypes: Set<PipelineStepType>,
  baseMessage: string
): CanEditReturnValue | null {
  if (stepTypes.has(PipelineStepType.UNION_DATASETS)) {
    return {
      canEdit: false,
      message: `${baseMessage} it is used in a union.`,
    };
  }

  return null;
}

function checkOutputsSingleDataFrame(step: PipelineStep, baseMessage: string): CanEditReturnValue | null {
  if (
    step.outputStepDatas.length !== 1 ||
    getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, step.outputStepDatas[0]).length !== 1
  ) {
    return {
      canEdit: false,
      message: `${baseMessage} its source is not a table.`,
    };
  }
  return null;
}

/** Check if the analysis or template is archived and, if so, what user-facing message to show. */
export function checkIsAnalysisArchived(
  analysis: Pick<PipelineGraphQL, 'id' | 'name' | 'archiveRecord'>,
  message?: string
): CanEditReturnValue | null {
  if (isArchived(analysis)) {
    return {
      canEdit: false,
      message: message ?? getArchivedTooltipMessage(analysis),
    };
  }
  return null;
}

/**
 * Check if a step can be modified (or deleted) based on generic criteria:
 *
 * • If the analysis is archived, the step cannot be modified
 * • If a step is upstream from SAVE_TO_RESULTS, or other step that creates data outside the Analysis, the step cannot be modified
 * • If a step is upstream from a step type that does not support re-running, the step cannot be modified
 * • If a step is upstream from a step that creates a new top-level table–JOIN, UNION, or PROMOTE–the step cannot be deleted
 *
 * TODO(BNCH-125860): Add flag rows logic here
 *
 * @param analysis The analysis
 * @param stepId The step to modify (or delete)
 * @param userFacingTerm The user-facing term of what is being modified (or deleted)
 * @param modificationType Describe the type of modification being checked:
 *   - `'modifyStep'`: Directly modifying the step's configuration
 *   - `'delete'`: Deleting the step
 *   - `'editLineage'`: Adding steps on top of the current step without modifying it directly
 * @param checkAnalysisProperties Whether properties on the analysis object should be checked. Usually should
 * be left to true, but you may want to set this to false if these properties have already been checked and
 * you don't want to repeat a message.
 * @returns {canEdit} True if the step is safe to modify (or delete) based on general criteria
 * @returns {message} A user-facing message explaining why the step cannot be modified (or deleted)
 */
export function checkCanModifyStep(
  pipeline: Pick<PipelineGraphQL, 'id' | 'name' | 'steps' | 'permissions' | 'archiveRecord'>,
  stepId: string,
  userFacingTerm: 'view' | 'table' | 'step',
  modificationType: 'modifyStep' | 'delete' | 'editLineage',
  checkAnalysisProperties: boolean = true
): CanEditReturnValue {
  const step = getStepFromAnalysis(pipeline, stepId);

  const baseMessage = `This ${userFacingTerm} cannot be ${modificationType === 'delete' ? 'removed' : 'modified'} because`;

  const checks: Array<() => CanEditReturnValue | null> = [];
  if (checkAnalysisProperties) {
    checks.push(
      () => checkIsAnalysisArchived(pipeline),
      () => checkPermissions(pipeline)
    );
  }

  // The backend's isRerunnable/nonRerunnableReason already fold in the step's descendant lineage, so we
  // check the edited step directly rather than re-walking downstream steps here.
  // - modifyStep is blocked when the step is not rerunnable.
  // - delete/editLineage are structural: a non-executable step (e.g. NOTEBOOK_TABLE_DATASET) can be
  //   deleted or built on top of, but an output step type can't, regardless of rerunnability.
  if (modificationType === 'modifyStep') {
    checks.push(() => checkStepIsRerunnable(baseMessage, step));
  } else {
    checks.push(() => checkIsOutputStepType(baseMessage, new Set([step.stepType])));
  }

  if (modificationType === 'delete') {
    // The delete-specific structural rules still need the downstream step types.
    const stepTypes = new Set<PipelineStepType>(
      traversePipeline(pipeline, {
        startingSteps: [step],
        checkMatchFn: ({id}) => id !== step.id,
      }).map((downstreamStep) => downstreamStep.stepType)
    );
    checks.push(() => checkIsUsedInUnion(stepTypes, baseMessage));
    checks.push(() => checkIsUsedInJoin(stepTypes, baseMessage));
    checks.push(() => checkIsUsedToCreateNewTable(stepTypes, baseMessage));
    checks.push(() => checkNotRegressionOrRegressionNotUsed(baseMessage, pipeline, step));
  }
  for (const check of checks) {
    const result = check();
    if (result != null) {
      return result;
    }
  }

  return {canEdit: true, message: null};
}

/**
 * Check if a new step can be inserted after an existing step based on generic criteria.
 *
 * This is similar to `checkCanModifyStep`, but if `outputDataFrameId` is passed will only consider steps
 * downstream from a particular output data frame of a (multi-output) step.
 *
 * @param pipeline The pipeline
 * @param stepId The step to modify (or remove)
 * @param userFacingTerm The user-facing term of what is being modified (or removed)
 * @param outputDataFrameId If passed, only considers steps downstream from a particular output data from of the (multi-output) step
 * @returns {canEdit} True if it is safe to insert a new step after the current step based on general criteria
 * @returns {message} A user-facing message explaining why a step cannot be added
 */
export function checkCanInsertStep(
  pipeline: Pick<PipelineGraphQL, 'id' | 'name' | 'steps' | 'permissions' | 'archiveRecord'>,
  stepId: string,
  userFacingTerm: 'view' | 'table',
  outputDataFrameId?: string
): CanEditReturnValue {
  if (outputDataFrameId == null) {
    return checkCanModifyStep(pipeline, stepId, userFacingTerm, 'modifyStep');
  }

  // Find only the direct child steps that use the output data frame
  const step = getStepFromAnalysis(pipeline, stepId);
  const childSteps = step.outgoingEdges
    .map((edge) => getStepFromAnalysis(pipeline, edge.toStep.id))
    .filter((childStep) =>
      childStep.inputStepDatas.some((stepData) =>
        getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, stepData).some(
          ({id}) => id === outputDataFrameId
        )
      )
    );

  for (const childStep of childSteps) {
    const result = checkCanModifyStep(pipeline, childStep.id, userFacingTerm, 'modifyStep');
    if (!result.canEdit) {
      return result;
    }
  }

  return {canEdit: true, message: null};
}

/**
 * Check if a step can be replaced based on generic criteria:
 *
 * • If the analysis is archived, the step cannot be replaced
 * • If a step is upstream from SAVE_TO_RESULTS, the step cannot be replaced
 * • If a step is a PROCEDURE_RUN_DATA_FRAMES or THIRD_PARTY step, it cannot be replaced (due to multi output steps, we will encourage them to use templates in the future.)
 *
 * @param pipeline The pipeline
 * @param step The step to modify (or remove)
 * @returns {canEdit} True if the step is safe to replace based on general criteria
 * @returns {message} A user-facing message explaining why the step cannot be modified (or removed)
 */
export function checkCanReplaceStep(
  pipeline: Pick<PipelineGraphQL, 'id' | 'name' | 'steps' | 'permissions' | 'archiveRecord'>,
  step: PipelineStep,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo>
): CanEditReturnValue {
  const downstreamSteps = traversePipeline(pipeline, {
    startingSteps: [step],
    checkMatchFn: ({id}) => id !== step.id, // Exclude `step` itself
  });
  const downstreamStepTypes = new Set<PipelineStepType>(downstreamSteps.map((step) => step.stepType));
  const allConnectedSteps = [
    ...downstreamSteps,
    ...traversePipeline(pipeline, {startingSteps: [step], traverseAncestors: true}),
  ];

  const checks = [
    () => checkIsAnalysisArchived(pipeline),
    () => checkPermissions(pipeline),
    () => checkHasDownstreamFlagRows(downstreamStepTypes),
    () => checkStepIsRerunnable('This table cannot be replaced because', step),
    () =>
      checkIsStepTypeUnsupported(
        step,
        new Set([PipelineStepType.PROCEDURE_RUN_DATA_FRAMES, PipelineStepType.THIRD_PARTY]),
        'Replace table',
        stepTypeInfos
      ),
    () => checkHasRunningStep(allConnectedSteps, 'This table cannot be replaced because'),
    () => checkOutputsSingleDataFrame(step, 'This table cannot be replaced because'),
  ];

  for (const check of checks) {
    const result = check();
    if (result != null) {
      return result;
    }
  }

  return {canEdit: true, message: null};
}
